import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import api from '../../services/api';
import { formatDateTimeFR } from '../../utils/dateUtils';

const STATUS_LABELS = {
  pending: { label: 'En attente', class: 'bg-yellow-100 text-yellow-800' },
  confirmed: { label: 'Confirmé', class: 'bg-green-100 text-green-800' },
  cancelled: { label: 'Annulé', class: 'bg-red-100 text-red-800' },
  completed: { label: 'Terminé', class: 'bg-slate-100 text-slate-600' },
};

const ParentAppointmentsPage = () => {
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ subject: '', message: '', proposed_date: '', proposed_time: '' });
  const [submitting, setSubmitting] = useState(false);

  const load = () => {
    api.get('/parent-portal/appointments')
      .then((res) => { if (res.data.success) setAppointments(res.data.data); })
      .catch(() => toast.error('Erreur de chargement'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const res = await api.post('/parent-portal/appointments', form);
      if (res.data.success) {
        toast.success(res.data.message);
        setShowForm(false);
        setForm({ subject: '', message: '', proposed_date: '', proposed_time: '' });
        load();
      } else {
        toast.error(res.data.message);
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Erreur');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-slate-900">Mes rendez-vous</h2>
          <p className="text-sm text-slate-500">Proposez un créneau — le secrétariat confirmera ou proposera un autre horaire</p>
        </div>
        <button
          onClick={() => setShowForm(!showForm)}
          className="px-4 py-2 bg-primary-600 text-white rounded-lg text-sm font-medium hover:bg-primary-700"
        >
          {showForm ? 'Annuler' : 'Nouveau RDV'}
        </button>
      </div>

      {showForm && (
        <form onSubmit={handleSubmit} className="bg-white rounded-xl border border-slate-200 p-5 space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Objet</label>
            <input
              value={form.subject}
              onChange={(e) => setForm({ ...form, subject: e.target.value })}
              className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
              placeholder="Ex : Rencontre avec le proviseur"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Message (optionnel)</label>
            <textarea
              value={form.message}
              onChange={(e) => setForm({ ...form, message: e.target.value })}
              className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
              rows={3}
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Date proposée</label>
              <input
                type="date"
                value={form.proposed_date}
                onChange={(e) => setForm({ ...form, proposed_date: e.target.value })}
                className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Heure proposée</label>
              <input
                type="time"
                value={form.proposed_time}
                onChange={(e) => setForm({ ...form, proposed_time: e.target.value })}
                className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
                required
              />
            </div>
          </div>
          <button
            type="submit"
            disabled={submitting}
            className="px-4 py-2 bg-primary-600 text-white rounded-lg text-sm font-medium disabled:opacity-50"
          >
            {submitting ? 'Envoi...' : 'Envoyer la demande'}
          </button>
        </form>
      )}

      {loading ? (
        <div className="flex justify-center py-8">
          <div className="animate-spin w-6 h-6 border-2 border-primary-600 border-t-transparent rounded-full" />
        </div>
      ) : appointments.length === 0 ? (
        <div className="bg-white rounded-xl p-8 text-center text-slate-500">Aucun rendez-vous</div>
      ) : (
        <div className="space-y-3">
          {appointments.map((a) => {
            const st = STATUS_LABELS[a.status] || STATUS_LABELS.pending;
            return (
              <div key={a.id} className="bg-white rounded-xl border border-slate-200 p-4">
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="font-medium text-slate-900">{a.subject}</h3>
                    {a.message && <p className="text-sm text-slate-500 mt-1">{a.message}</p>}
                    <p className="text-sm text-slate-600 mt-2">
                      Proposé : {formatDateTimeFR(a.proposed_date, a.proposed_time)}
                    </p>
                    {a.confirmed_date && (
                      <p className="text-sm font-medium text-green-700 mt-1">
                        Confirmé : {formatDateTimeFR(a.confirmed_date, a.confirmed_time)}
                      </p>
                    )}
                    {a.staff_notes && (
                      <p className="text-xs text-slate-500 mt-1 italic">Note : {a.staff_notes}</p>
                    )}
                  </div>
                  <span className={`text-xs px-2 py-1 rounded-full font-medium ${st.class}`}>{st.label}</span>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ParentAppointmentsPage;
